import { patchOf } from './patch';
import { z } from 'zod';
import { FINANCIAL_ACCOUNT_TYPES } from './finance';

const money = z.coerce.number().int().min(1, 'Informe um valor maior que zero.').max(100_000_000_00);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD.');
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mês no formato AAAA-MM.');
const percent = z.coerce.number().min(0).max(100);

export const createTransactionSchema = z.object({
  type: z.enum(['INCOME', 'EXPENSE']),
  categoryId: z.string().uuid(),
  accountId: z.string().uuid().optional(),
  description: z.string().trim().min(2).max(200),
  amountCents: money,
  competenceDate: day,
  dueDate: day.nullable().optional(),
  /** Já pago/recebido: informe quando (padrão: agora). Ausente = conta a pagar/receber. */
  paidAt: z.coerce.date().nullable().optional(),
  attachmentKey: z.string().max(300).nullable().optional(),
});
export type CreateTransactionInput = z.infer<typeof createTransactionSchema>;

export const updateTransactionSchema = patchOf(createTransactionSchema.omit({ type: true, paidAt: true }));

export const payTransactionSchema = z.object({
  paidAt: z.coerce.date().optional(),
  accountId: z.string().uuid().optional(),
});

export const listTransactionsQuerySchema = z.object({
  from: day.optional(),
  to: day.optional(),
  type: z.enum(['INCOME', 'EXPENSE']).optional(),
  status: z.enum(['PENDING', 'PAID', 'CANCELED']).optional(),
  categoryId: z.string().uuid().optional(),
  /** Contas a pagar/receber vencendo até esta data (com `status=PENDING`). */
  dueUntil: day.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const recurringExpenseSchema = z.object({
  categoryId: z.string().uuid(),
  accountId: z.string().uuid().optional(),
  description: z.string().trim().min(2).max(200),
  amountCents: money,
  dayOfMonth: z.coerce.number().int().min(1).max(31),
  endsAt: day.nullable().optional(),
});
export const updateRecurringSchema = patchOf(recurringExpenseSchema.pick({ amountCents: true, description: true, dayOfMonth: true, endsAt: true })).extend({
  active: z.boolean().optional(),
});

export const financeCategorySchema = z.object({
  name: z.string().trim().min(2).max(60),
  type: z.enum(['INCOME', 'EXPENSE']),
  dreGroup: z.enum(['GROSS_REVENUE', 'DEDUCTIONS', 'OPERATING_EXPENSES', 'FINANCIAL', 'TAXES']),
});

export const financialAccountSchema = z.object({
  name: z.string().trim().min(2).max(60),
  type: z.enum(FINANCIAL_ACCOUNT_TYPES),
});

export const monthQuerySchema = z.object({ month: month.optional() });
export const rangeQuerySchema = z
  .object({ from: day, to: day })
  .refine((r) => r.from <= r.to, { message: 'A data inicial deve ser antes da final.', path: ['from'] })
  .refine((r) => (Date.parse(r.to) - Date.parse(r.from)) / 86400_000 <= 366, { message: 'Período máximo de um ano.', path: ['to'] });

export const priceCalculatorSchema = z.object({
  costCents: z.coerce.number().int().min(0),
  /** Taxas de pagamento + plataforma em %. Ausente = média real das vendas. */
  feesPercent: percent.optional(),
  taxPercent: percent.default(0),
  shippingCents: z.coerce.number().int().min(0).default(0),
  marginPercent: percent,
});

export const financeUploadSchema = z.object({
  contentType: z.enum(['image/png', 'image/jpeg', 'image/webp', 'application/pdf']),
  size: z.coerce.number().int().positive().max(10 * 1024 * 1024, 'O arquivo pode ter no máximo 10 MB.'),
});
